import { Core } from "@faire/design-tokens";
import { SelectTeamUsersDropdown } from "@faire/retailer-visitor-shared/views/multi-user/Share/__internal__/SelectTeamUsersDropdown";
import { TOAST_AUTO_DISMISS_TIME_MS } from "@faire/retailer-visitor-shared/views/multi-user/Share/consts";
import { copyTextToClipboard } from "@faire/web--source/common/copyTextToClipboard";
import { getWebApiErrorMessage } from "@faire/web--source/common/getErrorMessage";
import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Button } from "@faire/web--source/slate/Button";
import { Link } from "@faire/web--source/slate/icons/foundation/Link";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Modal } from "@faire/web--source/slate/Modal";
import { Spacer } from "@faire/web--source/slate/spacing";
import { createToast } from "@faire/web--source/slate/Toast/Toast";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useMutation as useShareItemMutation } from "@faire/web-api--source/endpoints/www/api/retailer/team/share/post-hooks";
import { useQuery as useMyTeamUsersQuery } from "@faire/web-api--source/endpoints/www/api/retailer/team/share/users/get-hooks";
import { trackRetailerTeamManagementCopyLinkButtonClick } from "@faire/web-api--source/events/retailerTeamManagement/click/copyLinkButton";
import { trackRetailerTeamManagementSendEmailShareModalButtonClick } from "@faire/web-api--source/events/retailerTeamManagement/click/sendEmailShareModalButton";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IRetailerShareableItem } from "@faire/web-api--source/indigofair/retailer/IRetailerShareableItem";
import { IRetailerShareableUser } from "@faire/web-api--source/indigofair/retailer/IRetailerShareableUser";
import { IRetailerShareItemWithTeamMembersRequest } from "@faire/web-api--source/indigofair/retailer/IRetailerShareItemWithTeamMembersRequest";
import { ShareableItemType } from "@faire/web-api--source/indigofair/retailer/ShareableItemType";
import React, { useState } from "react";
import styled from "styled-components";

interface IProps {
  modalType: ShareableItemType;
  image: IImage | undefined;
  name: string | undefined;
  subText: string | undefined;
  token: string | undefined;
  onClose: () => void;
  pageName: string;
  showCopyLinkButton?: boolean;
  onInviteButtonClick?: () => void;
}

export const ShareWithTeamModal: React.FC<IProps> = ({
  modalType,
  image,
  name,
  subText,
  token,
  onClose,
  showCopyLinkButton,
  onInviteButtonClick,
  pageName,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [note, setNote] = React.useState("");
  const isSSR = useIsSSR();
  const viewport = useViewport();

  const [selectedUsers, setSelectedUsers] = useState<IRetailerShareableUser[]>(
    []
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [dropdownErrorMessage, setDropdownErrorMessage] = useState<
    string | undefined
  >(undefined);

  const { data, isPending: isLoadingTeamsData } = useMyTeamUsersQuery();
  const { mutateAsync: shareItem, isPending: isLoadingSendEmail } =
    useShareItemMutation();

  const getModalTitle = () => {
    switch (modalType) {
      case ShareableItemType.BRAND_ORDER:
        return strictLocalize({
          defaultMessage: "Share order",
          description: {
            text: "text for modal that lets user know that they are sharing an order",
            img: "https://cdn.faire.com/fastly/1012eb0c36e15845fbab825de2b077c8d6899e93604be3c258f77d57391ca95f.png",
          },
        });
      case ShareableItemType.INVOICE:
        return strictLocalize({
          defaultMessage: "Share invoice",
          description: {
            text: "text for modal that lets user know that they are sharing a invoice",
            img: "https://cdn.faire.com/fastly/f984f4220a39f215aecf486990f08fe24ac2860537cab8709f3c61aadb8ef05a.png",
          },
        });
      case ShareableItemType.PRODUCT:
        return strictLocalize({
          defaultMessage: "Share product",
          description: {
            text: "text for modal that lets user know that they are sharing a product",
            img: "https://cdn.faire.com/fastly/856f5c666f0a065c3322d8066883c196b63ebcceefceba2d87fae4ab375a4e29.png",
          },
        });
      default:
        return strictLocalize({
          defaultMessage: "Share brand",
          description: {
            text: "text for modal that lets user know that they are sharing a brand",
            img: "https://cdn.faire.com/fastly/fa3cd42bba8700c148499e56b5bcdef8d6bd64dc122a4f9456df24c70e1d82c6.png",
          },
        });
    }
  };

  const handleSendEmail = async () => {
    trackRetailerTeamManagementSendEmailShareModalButtonClick(pageName);

    if (selectedUsers.length === 0) {
      setDropdownErrorMessage(
        strictLocalize({
          defaultMessage: "Select team member(s) to share with",
          description: {
            text: "error message that lets user know that they need to select at least one team member",
            img: "https://cdn.faire.com/fastly/145000675956452cbfc1878a7175f36e922ae1b5ba5cdf6ac062fc948b527fe4.png",
          },
        })
      );
      return;
    } else {
      setDropdownErrorMessage(undefined);
    }

    try {
      await shareItem([
        IRetailerShareItemWithTeamMembersRequest.build({
          tokens_of_users_to_share_with: selectedUsers
            .map((user) => user.user_token)
            .filter(isNotUndefined),
          item: IRetailerShareableItem.build({
            type: modalType,
            note: note,
            token: token,
          }),
        }),
      ]);

      createToast(
        <Typography>
          <StrictLocalMsg
            defaultMessage="Sent successfully"
            description={{
              text: "text for toast that lets user know that they have sent an email",
            }}
          />
        </Typography>,
        {
          icon: "foundation:CheckOutline",
          timeout: TOAST_AUTO_DISMISS_TIME_MS,
        }
      );
      onClose();
    } catch (error) {
      setErrorMessage(getWebApiErrorMessage(error, strictLocalize));
    }
  };

  const handleCopyLink = () => {
    trackRetailerTeamManagementCopyLinkButtonClick(pageName);

    const window = getWindowOrThrow();
    copyTextToClipboard(window.location.href);

    createToast(
      <StrictLocalMsg
        defaultMessage="Link copied successfully"
        description={{
          text: "text for toast that lets user know that they have copied a link",
        }}
      />,
      {
        icon: "foundation:CheckOutline",
        timeout: TOAST_AUTO_DISMISS_TIME_MS,
      }
    );
    onClose();
  };

  return (
    <Modal
      padding="88px 24px 24px 24px"
      width={viewport.isTabletAndAbove ? "420px" : undefined}
      onClose={onClose}
      align="stretch"
      contentStyle={{
        overflow: "visible",
      }}
      data-test-id="share-with-team-modal"
    >
      <Typography variant="pageHeaderSerifRegular">
        {getModalTitle()}
      </Typography>
      <Spacer size="2x" />
      {isLoadingTeamsData ? (
        <LoadingSpinnerContainer data-test-id="share-with-team-loading-spinner">
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      ) : (
        <>
          {modalType === ShareableItemType.BRAND ||
          modalType === ShareableItemType.PRODUCT ? (
            <>
              <Row align="center">
                <img
                  src={optimizeImageUrlFillSquare(
                    { url: image?.url },
                    isSSR,
                    96
                  )}
                  style={{
                    width: "96px",
                    height: "96px",
                  }}
                />
                <Spacer size="2x" />
                <Column>
                  <Typography>{name}</Typography>
                  <Spacer size={4} />
                  <Typography color={Core.text.subdued}>{subText}</Typography>
                </Column>
              </Row>
              <Spacer height="2x" />
            </>
          ) : null}
          <SelectTeamUsersDropdown
            options={data?.users ?? []}
            selectedUsers={selectedUsers}
            onSelect={setSelectedUsers}
            pageName={pageName}
            onInviteButtonClick={onInviteButtonClick}
            errorMessage={dropdownErrorMessage}
            setErrorMessage={setDropdownErrorMessage}
          />
          {selectedUsers.length ? <Spacer height="1x" /> : null}
          <div>
            <TextArea
              data-test-id="share-with-team-note-input"
              label={strictLocalize({
                defaultMessage: "Note (optional)",
                description: {
                  text: "text for label that lets user know that they can add a note",
                  img: "https://cdn.faire.com/fastly/856f5c666f0a065c3322d8066883c196b63ebcceefceba2d87fae4ab375a4e29.png",
                },
              })}
              onChange={(e) => setNote(e.target.value)}
              placeholder={strictLocalize({
                defaultMessage: "Add a note to help your team jump right in",
                description: {
                  text: "text for placeholder that lets user know that they can add a note",
                  img: "https://cdn.faire.com/fastly/856f5c666f0a065c3322d8066883c196b63ebcceefceba2d87fae4ab375a4e29.png",
                },
              })}
              style={{ height: "100px", maxHeight: "100px" }}
              value={note}
            />
          </div>
          {errorMessage ? (
            <>
              <Spacer size="1x" />
              <Typography color={Core.text.critical}>{errorMessage}</Typography>
            </>
          ) : null}
          <Spacer height="4x" />
          <ButtonWrapper>
            <Button
              onClick={handleSendEmail}
              disabled={isLoadingSendEmail}
              data-test-id="send-email-button"
            >
              <StrictLocalMsg
                defaultMessage="Send email"
                description={{
                  text: "text for button that lets user know that they share via email",
                  img: "https://cdn.faire.com/fastly/856f5c666f0a065c3322d8066883c196b63ebcceefceba2d87fae4ab375a4e29.png",
                }}
              />
            </Button>
            <Spacer size="2x" />
            {showCopyLinkButton ? (
              <Button variant="tertiary" onClick={handleCopyLink}>
                <Link />
                <Spacer size={4} />
                <StrictLocalMsg
                  defaultMessage="Copy share link"
                  description={{
                    text: "text for button that lets user know that they can copy a share link",
                    img: "https://cdn.faire.com/fastly/856f5c666f0a065c3322d8066883c196b63ebcceefceba2d87fae4ab375a4e29.png",
                  }}
                />
              </Button>
            ) : null}
          </ButtonWrapper>
        </>
      )}
    </Modal>
  );
};

const ButtonWrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})``;

const LoadingSpinnerContainer = styled(Row).attrs({
  align: "center",
  justify: "center",
})`
  flex-grow: 1;
  width: 100%;
  margin: 100px 0;
`;
